/**
 * File: TableControls.js
 * Origin: isotect
 * Description: TableControls component for to render table control buttons.
 *
 * Version: v1.1.0
 * Created: 2026-04-30
 * Last Updated: 2026-07-21
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Local project files
import { exportTableToExcel } from "../../utils/tableUtilities";
import { IconButton, ToggleButtons } from "../ui/Buttons";
import FilterButton from "./FilterButton";
import SearchButton from "./SearchButton";

/**
 * Renders controls for a table, including view selection, search, and filters.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {number} props.tableId - HTML table ID
 * @param {Object} props.schema - Table controls configuration schema
 * @param {(view: string) => void} props.setView - Callback that updates the current table view
 * @param {string} props.query - Current search query
 * @param {(query: string) => void} props.setQuery - Callback that updates the search query
 * @param {Object} props.filters - Current filter values
 * @param {(filters: Object) => void} props.setFilters - Callback that updates the active filters
 *
 * @returns {JSX.Element} A set of table controls
 */
function TableControls({
    tableId,
    schema,
    setView,
    query,
    setQuery,
    filters,
    setFilters,
}) {
    const handleExcelExport = () => {
        exportTableToExcel(tableId);
    };

    return (
        <div className="table-controls">
            <div className="table-controls__left-controls">
                <ToggleButtons
                    buttons={schema.toggleButtons}
                    onClick={setView}
                />
            </div>
            <div className="table-controls__right-controls">
                <IconButton {...schema.iconButtons.add} onClick={() => null} />
                <SearchButton
                    schema={schema.iconButtons.search}
                    query={query}
                    setQuery={setQuery}
                />
                <FilterButton
                    schema={schema.filterButton}
                    filters={filters}
                    setFilters={setFilters}
                />
                <IconButton
                    {...schema.iconButtons.export}
                    onClick={handleExcelExport}
                />
            </div>
        </div>
    );
}

export default TableControls;
